/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { Box, Typography, Button, useMediaQuery, CircularProgress, Snackbar, Alert } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useCart } from "../../context/cartContext";
import { useSite } from "../../context/siteContext";
import "./ProductList.css";
import customHttp from "../../services/customHTTP";
import { useAuth } from "../../context/authContext";
import { ASSETS_BASE_URL, RSPL_LIST } from "../../config";

const ProductList = ({ products, onAddToCart, onRemoveProduct, showDelete }) => {
	const rsplMapping = Object.fromEntries(RSPL_LIST.flatMap(({ key, value }) => key.map((k) => [k, value])));
	const { addToCart } = useCart();
	const { selectedSite, currencySymbol } = useSite();
	const defaultImageSrc = `${ASSETS_BASE_URL}/stock.png`;
	const [productImages, setProductImages] = useState({});
	const isMobile = useMediaQuery("(max-width:600px)");
	const [loadingStates, setLoadingStates] = useState({}); // Track loading states by product ID
	const [successStates, setSuccessStates] = useState({}); // Track success states by product ID
	const [alertisSuccess, setAlertIsSuccess] = useState(false); // To track success state
	const [openAlert, setOpenAlert] = useState(false); // To control the alert visibility
	const [alertMessage, setAlertMessage] = useState("");
	const { user } = useAuth();
	useEffect(() => {
		const fetchProductImages = async () => {
			const updatedImages = {};

			for (const product of products) {
				const productImageUrl = `${ASSETS_BASE_URL}/ProductImages/TMB/${product.product_number}.png`;

				try {
					const response = await fetch(productImageUrl, { method: "HEAD" });
					updatedImages[product.product_id] = response.ok ? productImageUrl : defaultImageSrc;
				} catch (error) {
					console.warn(`Image not found for product_id: ${product.product_id}, using default image.`);
					updatedImages[product.product_id] = defaultImageSrc;
				}
			}

			setProductImages(updatedImages);
		};

		fetchProductImages();
	}, [products]);

	const handleAddToRequest = async (product) => {
		setLoadingStates((prev) => ({ ...prev, [product.product_id]: true }));
		setSuccessStates((prev) => ({ ...prev, [product.product_id]: false }));
		setTimeout(() => {
			// Set success state
		}, 2000);

		const req_body = {
			machine_id: product.machine_id,
			product_id: product.product_id,
			site_number: selectedSite,
			user_id: user.user_id,
		};

		try {
			const response = await customHttp.post("/request_list/saverequestlist", req_body);
			if (response.data.success) {
				setLoadingStates((prev) => ({ ...prev, [product.product_id]: false }));

				setSuccessStates((prev) => ({ ...prev, [product.product_id]: true }));
				setAlertIsSuccess(true);
				setOpenAlert(true);
				setAlertIsSuccess(true);
				setAlertMessage(`Product ${product.product_name} added to Request List`);
			} else if (!response.data.success) {
				setOpenAlert(true);
				setAlertIsSuccess(false);
				setAlertMessage(`Product ${product.product_name} already in Request List`);
			}

			setTimeout(() => {
				setLoadingStates((prev) => ({ ...prev, [product.product_id]: false }));
				setSuccessStates((prev) => ({ ...prev, [product.product_id]: false }));
			}, 2000);
		} catch (error) {
			console.error("Error fetching product name:", error);
		}

		// Reset the button text back to "Add to Cart" after 2 seconds
	};

	const handleAddToCart = async (product) => {
		// Start loading for this specific product
		setLoadingStates((prev) => ({ ...prev, [product.product_id]: true }));
		setSuccessStates((prev) => ({ ...prev, [product.product_id]: false }));

		try {
			// Call the addToCart function
			await addToCart({ product_id: product.product_id, Quantity: 1, Site: selectedSite });

			// Set success state for this product
			setSuccessStates((prev) => ({ ...prev, [product.product_id]: true }));
			setAlertIsSuccess(true);
			setAlertMessage(`Product ${product.product_name} added to Cart`);
			setOpenAlert(true);

			// Reset states for this product after 2 seconds
			setTimeout(() => {
				setLoadingStates((prev) => ({ ...prev, [product.product_id]: false }));
				setSuccessStates((prev) => ({ ...prev, [product.product_id]: false }));
				setOpenAlert(false);
			}, 2000);
		} catch (error) {
			setSuccessStates((prev) => ({ ...prev, [product.product_id]: false }));
			setAlertMessage(`Error adding ${product.product_name} to Cart`);
			setOpenAlert(true);
		} finally {
			setLoadingStates((prev) => ({ ...prev, [product.product_id]: false }));
		}
	};

	return (
		<>
			<Box width="100%">
				{products.map((product) => (
					<Box
						key={product.product_id}
						display="flex"
						flexDirection={isMobile ? "column" : "row"}
						justifyContent="space-between"
						alignItems="center"
						borderBottom="1px solid #ddd"
						padding="10px 0">
						<Box display="flex">
							<Box display="flex" justifyContent="flex-start" alignItems="center">
								<img
									src={productImages[product.product_id] || defaultImageSrc}
									alt={product.product_name}
									style={{ maxWidth: "100px", maxHeight: "100px" }}
								/>
							</Box>

							{/* Middle Column - Product Details */}
							<Box padding="0 20px">
								<Typography variant="h6">{product.product_name}</Typography>
								<Typography variant="body2" color="textSecondary">
									<b>Part #:</b> {product.product_number}
								</Typography>
								<Typography variant="body2" color="textSecondary">
									<b>Part Type:</b>{" "}
									{rsplMapping[product.product_rspl_code]
										? rsplMapping[product.product_rspl_code]
										: "Other"}
								</Typography>
								<Typography variant="h6" mt={1} color="primary">
									{product.purchasable === 1 ? currencySymbol + product.product_sell_price : ""}
									{/* {currencySymbol}
									{product.product_sell_price} */}
								</Typography>
							</Box>
						</Box>
						{/* Right Column - Add to Cart and Delete Buttons */}
						<Box
							sx={{ width: isMobile ? "100%" : "auto" }}
							display="flex"
							justifyContent={isMobile ? "space-between" : "center"}
							marginTop={isMobile ? "10px" : "0px"}
							alignItems="center"
							gap="4px"
							paddingRight="4px">
							{product.purchasable === 1 ? (
								<Button
									size="small"
									variant="contained"
									sx={{
										backgroundColor: "var(--primary-main)",
										padding: "8px 15px",
										fontWeight: "bolder",
									}}
									onClick={(event) => {
										event.stopPropagation();
										handleAddToCart(product);
									}}
									disabled={loadingStates[product.product_id]} // Disable the button while loading or after success
								>
									{loadingStates[product.product_id] ? (
										<>
											<CircularProgress size={16} sx={{ color: "white", marginRight: 1 }} />
											Adding...
										</>
									) : successStates[product.product_id] ? (
										<>
											<CheckCircleIcon sx={{ color: "white", marginRight: 1 }} />
											Added
										</>
									) : (
										"Add to Cart"
									)}
								</Button>
							) : (
								<Button
									size="small"
									variant="contained"
									sx={{
										backgroundColor: "var(--primary-main)",
										padding: "8px 15px",
										fontWeight: "bolder",
									}}
									onClick={(event) => {
										event.stopPropagation();
										handleAddToRequest(product);
									}}
									disabled={loadingStates[product.product_id]} // Disable the button while loading or after success
								>
									{loadingStates[product.product_id] ? (
										<>
											<CircularProgress size={16} sx={{ color: "white", marginRight: 1 }} />
											Adding...
										</>
									) : successStates[product.product_id] ? (
										<>
											<CheckCircleIcon sx={{ color: "white", marginRight: 1 }} />
											Added
										</>
									) : (
										"Add to Request List"
									)}
								</Button>
							)}
							{showDelete && (
								<Button
									variant="outlined"
									onClick={() => onRemoveProduct(product.product_id)}
									color="primary">
									<DeleteOutlineIcon />
								</Button>
							)}
						</Box>
					</Box>
				))}
			</Box>
			<Snackbar
				open={openAlert}
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
				autoHideDuration={3000}
				onClose={() => setOpenAlert(false)}
				className="SnackBar">
				<Alert severity={alertisSuccess ? "success" : "error"} sx={{ width: "100%" }}>
					{alertMessage}
				</Alert>
			</Snackbar>
		</>
	);
};

export default ProductList;
